import { useEffect } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import type {
  AccountMappingTemplate,
  MappingTemplateFormData,
} from "~/types/contabilidad";

const formSchema = z.object({
  company_id: z.string().min(1, "Seleccione una empresa"),
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  description: z.string().optional(),
  is_active: z.boolean().optional(),
});

interface MapeoTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: AccountMappingTemplate;
  onSubmit: (data: MappingTemplateFormData) => void;
  isLoading?: boolean;
  defaultCompanyId?: string;
}

export function MapeoTemplateDialog({
  open,
  onOpenChange,
  template,
  onSubmit,
  isLoading = false,
  defaultCompanyId,
}: MapeoTemplateDialogProps) {
  const isEditing = !!template;
  const { data: companies = [], isLoading: isLoadingCompanies } = useCompanies();

  const form = useForm<MappingTemplateFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_id: template?.company_id ?? defaultCompanyId ?? "",
      name: template?.name ?? "",
      description: template?.description ?? "",
      is_active: template?.is_active ?? true,
    },
  });

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open) {
      form.reset({
        company_id: template?.company_id ?? defaultCompanyId ?? "",
        name: template?.name ?? "",
        description: template?.description ?? "",
        is_active: template?.is_active ?? true,
      });
    }
  }, [open, template, defaultCompanyId, form]);

  const handleSubmit = (data: MappingTemplateFormData) => {
    onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Plantilla de Mapeo" : "Nueva Plantilla de Mapeo"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Modifique los datos de la plantilla."
              : "Complete los datos para crear una nueva plantilla de mapeo."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="company_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Empresa *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={isLoadingCompanies || isEditing}
                  >
                    <FormControl>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Seleccione una empresa" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre de la Plantilla *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ej: Balance consolidado Uno"
                      className="rounded-xl"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripcion</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describa brevemente el proposito de esta plantilla..."
                      className="min-h-[80px] resize-none rounded-xl"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Plantilla Activa</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                className="rounded-full px-6"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : isEditing ? (
                  "Actualizar"
                ) : (
                  "Crear Plantilla"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
